/**
 * DebateSummaryPanel - Displays AI council debate arguments and mediator consensus.
 *
 * Groups debate arguments by round, color-codes debater roles,
 * and shows the mediator's final consensus at the bottom.
 */

import { useState, useMemo } from 'react';
import { Scale, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { DebateArgument, MediatorConsensus } from '@/hooks/useStrategicStream';

// Color mapping for debater roles (fallback by index for unknown roles)
const ROLE_COLORS = [
  { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', dot: 'bg-purple-400' },
  { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-400' },
  { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
];

interface DebateSummaryPanelProps {
  debateArguments: DebateArgument[];
  mediatorConsensus?: MediatorConsensus;
  debateRounds?: number;
  className?: string;
}

export function DebateSummaryPanel({
  debateArguments,
  mediatorConsensus,
  debateRounds: _debateRounds = 0,
  className,
}: DebateSummaryPanelProps) {
  void _debateRounds; // reserved for future use
  const [expandedRounds, setExpandedRounds] = useState<Record<number, boolean>>({});

  // Group arguments by round
  const roundGroups = useMemo(() => {
    const groups: Record<number, DebateArgument[]> = {};
    for (const arg of debateArguments) {
      if (!groups[arg.roundNumber]) groups[arg.roundNumber] = [];
      groups[arg.roundNumber].push(arg);
    }
    return Object.entries(groups)
      .map(([round, args]) => ({ round: Number(round), args }))
      .sort((a, b) => a.round - b.round);
  }, [debateArguments]);

  // Build stable role → color index mapping
  const roleColorMap = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    for (const arg of debateArguments) {
      const key = arg.debaterRole || arg.debaterName;
      if (!map.has(key)) {
        map.set(key, idx % ROLE_COLORS.length);
        idx++;
      }
    }
    return map;
  }, [debateArguments]);

  const toggleRound = (round: number) => {
    setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }));
  };

  if (debateArguments.length === 0 && !mediatorConsensus) {
    return (
      <div className="text-center py-8 text-text-muted text-sm">
        No debate data available for this analysis.
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Debate rounds */}
      {roundGroups.map(({ round, args }) => {
        const isExpanded = expandedRounds[round] ?? (round === roundGroups[roundGroups.length - 1]?.round);

        return (
          <div key={round} className="rounded-lg bg-surface/50 border border-border/50">
            {/* Round header */}
            <button
              onClick={() => toggleRound(round)}
              aria-expanded={isExpanded}
              aria-controls={`debate-round-content-${round}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-light/30 transition-colors rounded-lg"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-text-muted" />
                <span className="text-sm font-medium text-text-primary">Round {round}</span>
                <span className="text-xs text-text-muted">{args.length} arguments</span>
              </div>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-text-muted" />
              ) : (
                <ChevronDown className="w-4 h-4 text-text-muted" />
              )}
            </button>

            {/* Arguments */}
            {isExpanded && (
              <div id={`debate-round-content-${round}`} role="region" className="px-4 pb-4 space-y-3">
                {args.map((arg, i) => {
                  const colorIdx = roleColorMap.get(arg.debaterRole || arg.debaterName) ?? 0;
                  const color = ROLE_COLORS[colorIdx];

                  return (
                    <div
                      key={i}
                      className={cn(
                        'rounded-lg border p-3',
                        color.bg,
                        color.border,
                      )}
                    >
                      {/* Debater header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={cn('w-2 h-2 rounded-full', color.dot)} />
                          <span className={cn('text-sm font-medium', color.text)}>
                            {arg.debaterName || arg.debaterRole}
                          </span>
                        </div>
                        <span className="text-xs text-text-muted">
                          Confidence: {Math.round(arg.confidence * 100)}%
                        </span>
                      </div>

                      {/* Position */}
                      {arg.position && (
                        <div className="mb-2 px-2 py-1 rounded bg-surface-light/50 text-xs text-text-primary font-medium inline-block">
                          {arg.position}
                        </div>
                      )}

                      {/* Argument */}
                      <div className="text-sm text-text-primary leading-relaxed">
                        <MarkdownContent content={arg.argument} className="text-sm" />
                      </div>

                      {/* Evidence and counterpoints */}
                      <div className="mt-2 flex flex-col gap-1">
                        {arg.evidenceCited.length > 0 && (
                          <div className="text-xs text-text-muted">
                            <span className="font-medium text-text-secondary">Evidence:</span>{' '}
                            {arg.evidenceCited.join(' | ')}
                          </div>
                        )}
                        {arg.counterPoints.length > 0 && (
                          <div className="text-xs text-text-muted">
                            <span className="font-medium text-text-secondary">Counters:</span>{' '}
                            {arg.counterPoints.join(' | ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Mediator consensus */}
      {mediatorConsensus && (
        <div className="rounded-xl bg-gradient-to-r from-v-teal/10 to-v-gold/5 border border-v-teal/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-v-teal-light" />
              <span className="text-xs font-medium text-v-teal-light uppercase tracking-wide">
                Mediator Consensus
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                mediatorConsensus.consensusReached
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-amber-500/20 text-amber-400'
              )}>
                {mediatorConsensus.consensusReached ? 'Consensus Reached' : 'No Clear Consensus'}
              </span>
              <span className="text-xs text-text-muted">
                {Math.round(mediatorConsensus.confidence * 100)}%
              </span>
            </div>
          </div>

          {/* Winning position */}
          {mediatorConsensus.winningPosition && (
            <div className="mb-3 text-sm text-text-primary font-medium">
              {mediatorConsensus.winningPosition}
            </div>
          )}

          {/* Key agreements */}
          {mediatorConsensus.keyAgreements.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-v-teal/80 mb-1.5">Key Agreements</div>
              <ul className="space-y-1">
                {mediatorConsensus.keyAgreements.map((agreement, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{agreement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unresolved tensions */}
          {mediatorConsensus.unresolvedTensions.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-amber-400/80 mb-1.5">Unresolved Tensions</div>
              <ul className="space-y-1">
                {mediatorConsensus.unresolvedTensions.map((tension, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span>{tension}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Final recommendation */}
          {mediatorConsensus.finalRecommendation && (
            <div className="pt-3 border-t border-v-teal/10">
              <div className="text-xs font-medium text-text-secondary mb-1.5">Final Recommendation</div>
              <div className="text-sm text-text-primary leading-relaxed">
                <MarkdownContent content={mediatorConsensus.finalRecommendation} className="text-sm" />
              </div>
            </div>
          )}

          {/* Rationale */}
          {mediatorConsensus.rationale && (
            <div className="mt-2 text-xs text-text-muted leading-relaxed">
              <MarkdownContent content={mediatorConsensus.rationale} className="text-xs" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
